import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LayoutGrid,
  Disc3,
  Camera,
  UserPlus,
  Check,
  Clock,
  Pencil,
  Ghost,
} from "lucide-react";
import PrivacyGate from "../components/PrivacyGate";
import BraceletProfileCard from "../components/BraceletProfileCard";
import {
  StageRig,
  buildRigBeams,
  StageHeader,
  DJDeck,
  NowPlayingCard,
  KandiOrbit,
  FestivalPassport,
  PhotoGallery,
  KandiCard,
  Label,
  Divider,
  GENRE_GLOW,
} from "./UserDashboard";
import { T } from "../theme";

// ─── Design tokens ────────────────────────────────────────────────────────────

const API_URL = process.env.REACT_APP_API_URL || "";

const VIS_BADGE = {
  private: { emoji: "🔒", label: "Private" },
  friends: { emoji: "🤝", label: "Friends Only" },
  public: { emoji: "🌐", label: "Public" },
};

// ─── Read-only stage showcase ─────────────────────────────────────────────────
// The same rig / deck / orbit / passport the owner sees on their dashboard —
// their lights, their genre, their anthem — just without any edit controls.

function ReadOnlyStage({ profile, onKandiClick, viewerId }) {
  const rigBeams = useMemo(
    () =>
      buildRigBeams(
        profile.stageConfig?.beamColors,
        profile.stageConfig?.genre,
      ),
    [profile.stageConfig],
  );
  const glowColor = GENRE_GLOW[profile.stageConfig?.genre] || null;
  const kandis = profile.kandis || [];
  const totalScans = kandis.reduce((s, k) => s + (k.scanCount || 0), 0);
  const deckUser = {
    username: profile.username,
    joinedDate: profile.joinedDate,
    bio: profile.bio,
  };

  return (
    <div
      className="stage-root"
      style={{ position: "relative", paddingTop: 36 }}
    >
      <StageRig beams={rigBeams} />
      <StageHeader />

      <div
        className="dj-deck-wrapper"
        style={{ position: "relative", zIndex: 2, marginBottom: 14 }}
      >
        <DJDeck
          user={deckUser}
          kandiCount={kandis.length}
          totalScans={totalScans}
          friendCount={profile.friendCount || 0}
          collection={kandis}
          onKandiClick={onKandiClick}
          glowColor={glowColor}
        />
      </div>

      <div className="stage-columns">
        <div className="stage-col">
          <NowPlayingCard
            nowPlaying={profile.stageConfig?.nowPlaying}
            readOnly
          />

          <div
            className="kandi-orbit-card"
            style={{
              background: T.card,
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: T.border,
              borderRadius: 24,
              padding: "22px 16px",
              position: "relative",
              zIndex: 2,
            }}
          >
            <KandiOrbit
              collection={kandis}
              user={{ username: profile.username }}
              onKandiClick={onKandiClick}
              glowColor={glowColor}
            />
          </div>
        </div>

        <div className="stage-col">
          <div
            style={{
              background: T.card,
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: T.border,
              borderRadius: 24,
              padding: "22px 22px",
              position: "relative",
              zIndex: 2,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <Camera
                size={14}
                color={T.pink}
                style={{ filter: `drop-shadow(0 0 6px ${T.pink})` }}
              />
              <Label>Backstage Photos</Label>
            </div>
            <Divider />
            <PhotoGallery
              photos={profile.photos || []}
              apiUrl={API_URL}
              userId={viewerId}
            />
          </div>

          <div style={{ position: "relative", zIndex: 2 }}>
            <FestivalPassport collection={kandis} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function PublicProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const viewer = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "null"),
    [],
  );

  const [header, setHeader] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("stage");
  const [stageData, setStageData] = useState(null);
  const [collectionData, setCollectionData] = useState(null);
  const [selectedKandi, setSelectedKandi] = useState(null);
  const [loading, setLoading] = useState(true);

  const viewerParam = viewer?.id ? `?viewerId=${viewer.id}` : "";

  const fetchAll = useCallback(async () => {
    try {
      const headerRes = await axios.get(
        `${API_URL}/api/user/by-username/${encodeURIComponent(username)}${viewerParam}`,
      );
      const u = headerRes.data.user;
      setHeader(u);
      setNotFound(false);

      const [profileRes, kandiRes] = await Promise.all([
        axios
          .get(`${API_URL}/api/user/${u.id}/profile${viewerParam}`)
          .catch(() => null),
        axios
          .get(`${API_URL}/api/user/${u.id}/kandi${viewerParam}`)
          .catch(() => null),
      ]);
      setStageData(profileRes?.data || null);
      setCollectionData(kandiRes?.data || null);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else console.error("Profile fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [username, viewerParam]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Refetch on window focus — if a friend request got accepted elsewhere, both
  // tabs unlock without a full page reload
  useEffect(() => {
    const onFocus = () => fetchAll();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchAll]);

  const handleAddFriend = async () => {
    if (!viewer?.id) {
      navigate("/login");
      return;
    }
    try {
      await axios.post(`${API_URL}/api/friends/request`, {
        userId: viewer.id,
        targetUserId: header.id,
      });
      setHeader((h) => ({ ...h, relationship: "pending" }));
    } catch (err) {
      console.error("Friend request error:", err);
    }
  };

  const handleKandiClick = async (kandi) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/kandi/${kandi.id}${viewerParam}`,
      );
      setSelectedKandi(res.data);
    } catch (err) {
      console.error("Kandi fetch error:", err);
    }
  };

  // ── Loading / not found ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="animate-neon-pulse"
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${T.purple}, ${T.cyan})`,
          }}
        />
      </div>
    );
  }

  if (notFound || !header) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          style={{
            background: T.card,
            border: T.border,
            borderRadius: 24,
            padding: "48px 32px",
            textAlign: "center",
            maxWidth: 380,
          }}
        >
          <Ghost
            size={40}
            color="rgba(255,255,255,0.15)"
            style={{ marginBottom: 14 }}
          />
          <p
            style={{ color: T.textPrimary, fontWeight: 800, margin: "0 0 6px" }}
          >
            This raver doesn't exist
          </p>
          <p style={{ color: T.textMuted, fontSize: "0.88rem", margin: 0 }}>
            @{username} isn't on KandiKonnect (yet)
          </p>
        </div>
      </div>
    );
  }

  const isOwn = header.relationship === "self";
  const isFriend = header.relationship === "friends";
  const visBadge = VIS_BADGE[header.profileVisibility] || VIS_BADGE.private;
  const gateProps = {
    isFriend,
    onAddFriend: handleAddFriend,
    requestState: header.relationship === "pending" ? "pending" : "none",
  };

  // Data shaping for the kandi detail modal (mirrors UserDashboard)
  const modalKandi = selectedKandi
    ? {
        ...selectedKandi,
        ownerCount: selectedKandi.history?.length ?? 0,
        scannedDate: selectedKandi.createdAt
          ? new Date(selectedKandi.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "—",
        currentOwner:
          selectedKandi.currentOwner?.username ||
          selectedKandi.history?.[0]?.username,
      }
    : null;

  const modalHistory = (selectedKandi?.history || []).map((h, i) => ({
    username: h.username || "Unknown",
    date: new Date(h.date || h.timestamp || Date.now()).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
        year: "numeric",
      },
    ),
    location: h.location || "Unknown location",
    isCurrent: i === 0,
  }));

  return (
    <div
      className="dashboard-root"
      style={{
        minHeight: "100vh",
        background: T.bg,
        padding: "80px 16px 56px",
      }}
    >
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        {/* ── Header card ─────────────────────────────────────────────────── */}
        <div
          style={{
            background: T.card,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: T.border,
            borderRadius: 24,
            padding: "22px 22px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: 62,
              height: 62,
              borderRadius: "50%",
              flexShrink: 0,
              background: `linear-gradient(135deg, ${T.purple}, ${T.pink})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.3rem",
              fontWeight: 800,
              color: "#fff",
              border: "2px solid rgba(255,255,255,0.18)",
              boxShadow: `0 0 22px ${T.purple}55`,
            }}
          >
            {header.username.slice(0, 2).toUpperCase()}
          </div>

          <div style={{ flex: 1, minWidth: 180 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <h1
                style={{
                  margin: 0,
                  fontFamily: "var(--font-display)",
                  fontSize: "1.35rem",
                  fontWeight: 700,
                  color: T.pink,
                  textShadow: `0 0 20px ${T.purple}60`,
                }}
              >
                {header.username}
              </h1>
              <span
                title={`${visBadge.label} profile`}
                style={{
                  padding: "3px 10px",
                  borderRadius: 20,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: T.textMuted,
                }}
              >
                {visBadge.emoji} {visBadge.label}
              </span>
              {isFriend && (
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: 20,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    background: "rgba(57,255,20,0.1)",
                    border: "1px solid rgba(57,255,20,0.3)",
                    color: T.green,
                  }}
                >
                  Friends ✓
                </span>
              )}
            </div>
            {header.joinedDate && (
              <p
                style={{
                  margin: "4px 0 0",
                  color: T.textMuted,
                  fontSize: "0.78rem",
                }}
              >
                Raving since{" "}
                {new Date(header.joinedDate).toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </p>
            )}
          </div>

          {/* CTA */}
          {isOwn ? (
            <button
              onClick={() =>
                navigate("/dashboard", { state: { tab: "profile" } })
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                padding: "11px 20px",
                minHeight: 44,
                borderRadius: 50,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(180,79,255,0.4)",
                color: T.textPrimary,
                fontWeight: 700,
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              <Pencil size={14} /> Edit Profile
            </button>
          ) : header.relationship === "pending" ? (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "11px 20px",
                borderRadius: 50,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: T.textMuted,
                fontWeight: 700,
                fontSize: "0.85rem",
              }}
            >
              <Clock size={14} /> Pending
            </span>
          ) : header.relationship === "incoming" ? (
            <button
              onClick={() => navigate("/friends")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                padding: "11px 20px",
                minHeight: 44,
                borderRadius: 50,
                background:
                  "linear-gradient(135deg, rgba(57,255,20,0.18), rgba(0,245,255,0.18))",
                border: "1px solid rgba(57,255,20,0.35)",
                color: T.green,
                fontWeight: 700,
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              <Check size={14} /> Respond to Request
            </button>
          ) : !isFriend ? (
            <button
              onClick={handleAddFriend}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                padding: "11px 20px",
                minHeight: 44,
                borderRadius: 50,
                border: "none",
                background: "linear-gradient(135deg, #ff3cac, #b44fff)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.85rem",
                cursor: "pointer",
                boxShadow: "0 4px 18px rgba(180,79,255,0.35)",
              }}
            >
              <UserPlus size={14} /> Send Friend Request
            </button>
          ) : null}
        </div>

        {/* ── Tabs ────────────────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 18,
            padding: 4,
            background: "rgba(255,255,255,0.03)",
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {[
            { key: "stage", label: "My Stage", Icon: Disc3 },
            { key: "collection", label: "Collection", Icon: LayoutGrid },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                flex: 1,
                padding: "12px 8px",
                minHeight: 44,
                border: "none",
                borderRadius: 10,
                background:
                  tab === key ? "rgba(180,79,255,0.18)" : "transparent",
                color: tab === key ? T.purple : T.textMuted,
                fontSize: "0.82rem",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                transition: "all 0.2s",
              }}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {/* ── My Stage tab ────────────────────────────────────────────────── */}
        {tab === "stage" &&
          (stageData?.restricted ? (
            <PrivacyGate visibility={stageData.visibility} {...gateProps} />
          ) : stageData?.profile ? (
            <ReadOnlyStage
              profile={stageData.profile}
              onKandiClick={handleKandiClick}
              viewerId={viewer?.id}
            />
          ) : (
            <p style={{ color: T.textMuted, textAlign: "center", padding: 40 }}>
              Couldn't load this stage — try again in a moment
            </p>
          ))}

        {/* ── Collection tab ──────────────────────────────────────────────── */}
        {tab === "collection" &&
          (collectionData?.restricted ? (
            <PrivacyGate
              visibility={collectionData.visibility}
              {...gateProps}
            />
          ) : (collectionData?.kandis || []).length === 0 ? (
            <div
              style={{
                background: T.card,
                border: T.border,
                borderRadius: 24,
                padding: "48px 24px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "2.4rem", marginBottom: 12 }}>🪬</div>
              <p style={{ color: T.textMuted, margin: 0, fontSize: "0.9rem" }}>
                No kandi on display yet
              </p>
            </div>
          ) : (
            <div
              className="kandi-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(255px, 1fr))",
                gap: 14,
              }}
            >
              {collectionData.kandis.map((kandi) => (
                <KandiCard
                  key={kandi.id}
                  kandi={kandi}
                  onClick={handleKandiClick}
                />
              ))}
            </div>
          ))}
      </div>

      {/* ── Kandi detail modal (read-only) ─────────────────────────────────── */}
      {selectedKandi && modalKandi && (
        <BraceletProfileCard
          kandi={modalKandi}
          history={modalHistory}
          onClose={() => setSelectedKandi(null)}
        />
      )}
    </div>
  );
}

export default PublicProfilePage;
